import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Typography, Chip, Button, Tabs, Tab, Grid, Paper, Stack, TextField, ToggleButton, MenuItem,
  ToggleButtonGroup, Tooltip, alpha, useTheme,
} from '@mui/material';
import { CheckCircle, HourglassEmpty, ErrorOutline, PauseCircleOutline, Close } from '@mui/icons-material';
import { DataGrid, GridColDef, type DataGridProps } from '@mui/x-data-grid';
import { LocalizationProvider, DatePicker, type DateValidationError } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { adminDataGridSx } from '../../components/ui/adminDataGridSx';
import { formatMoney } from '../../utils/currency';
import {
  getSettlements, getSettlementSummary, retrySettlement, Settlement, SettlementSummaryItem,
} from '../../services/business/settlementService';

const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';
const PAGE_SIZE = 20;

type StatusKey = 'settled' | 'pending' | 'failed' | 'on_hold';

const STATUS_KEYS = new Set<StatusKey>(['settled', 'pending', 'failed', 'on_hold']);

function statusFromSearchParam(raw: string | null): '' | StatusKey {
  return raw && STATUS_KEYS.has(raw as StatusKey) ? (raw as StatusKey) : '';
}

const STATUS_CARDS: { key: StatusKey; label: string; color: 'success' | 'warning' | 'error' | 'info'; icon: ReactNode }[] = [
  { key: 'settled', label: 'Settled', color: 'success', icon: <CheckCircle /> },
  { key: 'pending', label: 'Pending', color: 'warning', icon: <HourglassEmpty /> },
  { key: 'failed', label: 'Failed', color: 'error', icon: <ErrorOutline /> },
  { key: 'on_hold', label: 'On Hold', color: 'info', icon: <PauseCircleOutline /> },
];

const MODULE_LABELS: Record<string, string> = {
  transportation: 'Transport',
  food: 'Food Delivery',
  grocery: 'Grocery Delivery',
  on_demand: 'Home Services',
  porter: 'Porter',
  events: 'Events',
};
const MODULE_ORDER = Object.keys(MODULE_LABELS);

const PARTNER_TYPE_LABELS: Record<string, string> = {
  driver: 'Driver',
  merchant: 'Merchant',
  delivery_partner: 'Delivery Partner',
  service_provider: 'Service Provider',
  event_organizer: 'Event Organizer',
};

const STATUS_LABELS: Record<string, string> = {
  paid: 'Settled',
  settled: 'Settled',
  pending: 'Pending',
  processing: 'Processing',
  failed: 'Failed',
  on_hold: 'On Hold',
};

const STATUS_CHIP_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  paid: 'success',
  settled: 'success',
  pending: 'warning',
  processing: 'info',
  failed: 'error',
  on_hold: 'default',
};

const QUICK_RANGES = ['Today', 'Last 7 days', 'Last 30 days', 'This Month', 'All time'] as const;
type QuickRange = typeof QUICK_RANGES[number];

function titleCase(value: string) {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const moduleLabel = (key?: string) => (key ? MODULE_LABELS[key] || titleCase(key) : '—');
const partnerTypeLabel = (key?: string) => (key ? PARTNER_TYPE_LABELS[key] || titleCase(key) : '—');
const statusLabel = (key?: string) => (key ? STATUS_LABELS[key] || titleCase(key) : '—');

function quickRangeDates(range: QuickRange): { from: string; to: string } {
  const today = dayjs();
  const fmt = (d: dayjs.Dayjs) => d.format('YYYY-MM-DD');
  switch (range) {
    case 'Today': return { from: fmt(today), to: fmt(today) };
    case 'Last 7 days': return { from: fmt(today.subtract(6, 'day')), to: fmt(today) };
    case 'Last 30 days': return { from: fmt(today.subtract(29, 'day')), to: fmt(today) };
    case 'This Month': return { from: fmt(today.startOf('month')), to: fmt(today) };
    default: return { from: '', to: '' };
  }
}

function matchQuickRange(from: string, to: string): QuickRange | null {
  return QUICK_RANGES.find((r) => {
    const d = quickRangeDates(r);
    return d.from === from && d.to === to;
  }) ?? null;
}

function pickerError(field: 'from' | 'to', error: DateValidationError | null) {
  if (!error) return undefined;
  if (error === 'invalidDate') return `Enter a valid date (${DISPLAY_DATE_FORMAT})`;
  if (error === 'disableFuture') return 'Date cannot be in the future';
  if (field === 'from' && error === 'maxDate') return 'From cannot be after To';
  if (field === 'to' && error === 'minDate') return 'To cannot be before From';
  return 'Invalid date';
}

function selectableCardSx(selected: boolean, color: string) {
  return {
    p: 2,
    height: '100%',
    cursor: 'pointer',
    border: selected ? 2 : 1,
    borderColor: selected ? color : 'divider',
    bgcolor: selected ? alpha(color, 0.06) : 'background.paper',
    transition: 'box-shadow 0.15s ease, transform 0.15s ease',
    '&:hover': { boxShadow: 3, transform: 'translateY(-1px)' },
    '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 },
  };
}

export default function SettlementsPage() {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const tableRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [vertical, setVertical] = useState('');
  const [status, setStatus] = useState<'' | StatusKey>(() => statusFromSearchParam(searchParams.get('status')));
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fromPickerError, setFromPickerError] = useState<DateValidationError | null>(null);
  const [toPickerError, setToPickerError] = useState<DateValidationError | null>(null);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
  const rangeOrderError = fromDate && toDate && toDate < fromDate ? 'To cannot be before From' : undefined;
  const fromError = pickerError('from', fromPickerError) ?? rangeOrderError;
  const toError = pickerError('to', toPickerError) ?? rangeOrderError;
  const dateInvalid = !!(fromError || toError);
  const selectedRange = matchQuickRange(fromDate, toDate);
  const dateParams = useMemo(() => ({
    ...(fromDate ? { from: fromDate } : {}),
    ...(toDate ? { to: toDate } : {}),
    timezone,
  }), [fromDate, toDate, timezone]);
  const periodText = fromDate || toDate
    ? `${fromDate ? dayjs(fromDate).format(DISPLAY_DATE_FORMAT) : 'Beginning'} – ${toDate ? dayjs(toDate).format(DISPLAY_DATE_FORMAT) : 'Today'}`
    : 'All time';

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['settlement-summary', dateParams],
    queryFn: () => getSettlementSummary(dateParams),
    enabled: !dateInvalid,
    placeholderData: keepPreviousData,
  });

  const listParams = {
    ...dateParams,
    status: status || undefined,
    module: vertical || undefined,
    search: search.trim() || undefined,
    page: page + 1,
    limit: PAGE_SIZE,
  };
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['settlements', listParams],
    queryFn: () => getSettlements(listParams),
    enabled: !dateInvalid,
    placeholderData: keepPreviousData,
  });

  const lastTotal = useRef(0);
  if (data?.meta?.total != null) lastTotal.current = data.meta.total;
  const rowCount = data?.meta?.total ?? lastTotal.current;
  const listTotals = data?.meta?.totals;

  const retryMutation = useMutation({
    mutationFn: retrySettlement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      queryClient.invalidateQueries({ queryKey: ['settlement-summary'] });
    },
  });

  const statusMap = useMemo(
    () => Object.fromEntries((summary?.byStatus ?? []).map((s) => [s._id, s])) as Record<string, SettlementSummaryItem>,
    [summary],
  );
  const modules = useMemo(() => {
    const rows = [...(summary?.byModule ?? [])];
    const rank = (k: string) => (MODULE_ORDER.indexOf(k) === -1 ? 99 : MODULE_ORDER.indexOf(k));
    return rows.sort((a, b) => rank(a._id) - rank(b._id) || a._id.localeCompare(b._id));
  }, [summary]);
  const moduleMap = useMemo(() => Object.fromEntries(modules.map((m) => [m._id, m])), [modules]);
  const tabKeys = useMemo(() => {
    const keys = ['transportation', 'food', 'grocery', ...modules.map((m) => m._id)];
    if (vertical) keys.push(vertical);
    return Array.from(new Set(keys));
  }, [modules, vertical]);

  const statusTotalCount = STATUS_CARDS.reduce((s, c) => s + (statusMap[c.key]?.count ?? 0), 0);
  const statusTotalPayout = STATUS_CARDS.reduce((s, c) => s + (statusMap[c.key]?.totalPayout ?? 0), 0);
  const moduleTotalCount = modules.reduce((s, m) => s + m.count, 0);
  const moduleTotalPayout = modules.reduce((s, m) => s + m.totalPayout, 0);
  const reconciled = statusTotalCount === moduleTotalCount && Math.abs(statusTotalPayout - moduleTotalPayout) < 0.01;

  const scrollToTable = () => {
    requestAnimationFrame(() => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };
  const selectStatus = (key: StatusKey) => {
    setStatus((cur) => (cur === key ? '' : key));
    setPage(0);
    scrollToTable();
  };
  const selectModule = (key: string) => {
    setVertical((cur) => (cur === key ? '' : key));
    setPage(0);
    scrollToTable();
  };
  const applyQuickRange = (range: QuickRange) => {
    const d = quickRangeDates(range);
    setFromDate(d.from);
    setToDate(d.to);
    setFromPickerError(null);
    setToPickerError(null);
    setPage(0);
  };

  const rows = useMemo(
    () => (data?.data ?? []).map((r, i) => ({ ...r, serialNo: page * PAGE_SIZE + i + 1 })),
    [data, page],
  );

  const columns: GridColDef<Settlement & { serialNo: number }>[] = [
    { field: 'serialNo', headerName: 'S.No', width: 70, sortable: false },
    {
      field: 'resourceNumber',
      headerName: 'Reference',
      minWidth: 170,
      flex: 1,
      renderCell: (p) => (
        <Tooltip title={p.value || ''}>
          <Typography variant="body2" fontWeight={600} noWrap>{p.value}</Typography>
        </Tooltip>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 160,
      renderCell: (p) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2">{p.value ? dayjs(p.value).format(DISPLAY_DATE_FORMAT) : '—'}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">{p.value ? dayjs(p.value).format('hh:mm A') : ''}</Typography>
        </Box>
      ),
    },
    { field: 'module', headerName: 'Service', width: 160, valueGetter: (_v, row) => moduleLabel(row.module) },
    { field: 'partnerType', headerName: 'Partner Type', width: 160, valueGetter: (_v, row) => partnerTypeLabel(row.partnerType) },
    {
      field: 'payeeName',
      headerName: 'Payee',
      minWidth: 220,
      flex: 1.3,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ minWidth: 0, py: 0.5 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{p.row.payeeName || '—'}</Typography>
          {p.row.payeeContact && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">{p.row.payeeContact}</Typography>
          )}
        </Box>
      ),
    },
    { field: 'grossAmount', headerName: 'Gross', width: 130, align: 'right', headerAlign: 'right', valueFormatter: (v) => formatMoney(v) },
    {
      field: 'commissionAmount',
      headerName: 'Commission',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <Box sx={{ py: 0.5, textAlign: 'right', width: '100%' }}>
          <Typography variant="body2">{formatMoney(p.value)}</Typography>
          {Number(p.row.commissionPercent) > 0 && (
            <Typography variant="caption" color="text.secondary" display="block">{Number(p.row.commissionPercent)}%</Typography>
          )}
        </Box>
      ),
    },
    { field: 'payoutAmount', headerName: 'Payout', width: 130, align: 'right', headerAlign: 'right', valueFormatter: (v) => formatMoney(v) },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (p) => {
        const holdUntil = p.row.metadata?.holdUntil;
        const held = p.row.status === 'pending' && holdUntil && dayjs(holdUntil).isAfter(dayjs());
        const tip = p.row.status === 'failed' ? p.row.failureReason
          : p.row.status === 'on_hold' ? p.row.holdReason
            : held ? `Payout Hold until ${dayjs(holdUntil).format(`${DISPLAY_DATE_FORMAT} hh:mm A`)} (Site Settings → Payments). It can't be approved or paid before then.`
              : '';
        return (
          <Tooltip title={tip || ''}>
            <Box sx={{ py: 0.5 }}>
              <Chip label={statusLabel(p.value as string)} color={STATUS_CHIP_COLOR[p.value as string] || 'default'} size="small" />
              {held && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Held until {dayjs(holdUntil).format(DISPLAY_DATE_FORMAT)}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (p) => (['failed', 'pending', 'processing'].includes(p.row.status) ? (
        <Button size="small" onClick={() => retryMutation.mutate(p.row._id)} disabled={retryMutation.isPending}>
          Retry
        </Button>
      ) : null),
    },
  ];

  const filterChips = [
    status && { key: 'status', label: `Status: ${statusLabel(status)}`, clear: () => { setStatus(''); setPage(0); } },
    vertical && { key: 'module', label: `Service: ${moduleLabel(vertical)}`, clear: () => { setVertical(''); setPage(0); } },
    (fromDate || toDate) && { key: 'date', label: `Period: ${periodText}`, clear: () => applyQuickRange('All time') },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance"
        title="Commission Settlements"
        subtitle="Partner payouts for Transport, Food, Grocery, Home Services and Porter. Click a card to see its records."
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From"
              format={DISPLAY_DATE_FORMAT}
              value={fromDate ? dayjs(fromDate) : null}
              maxDate={toDate ? dayjs(toDate) : undefined}
              disableFuture
              onError={setFromPickerError}
              onChange={(v) => { setFromDate(v && v.isValid() ? v.format('YYYY-MM-DD') : ''); setPage(0); }}
              slotProps={{ textField: { size: 'small', error: !!fromError, helperText: fromError || ' ' }, field: { clearable: true } }}
            />
            <DatePicker
              label="To"
              format={DISPLAY_DATE_FORMAT}
              value={toDate ? dayjs(toDate) : null}
              minDate={fromDate ? dayjs(fromDate) : undefined}
              disableFuture
              onError={setToPickerError}
              onChange={(v) => { setToDate(v && v.isValid() ? v.format('YYYY-MM-DD') : ''); setPage(0); }}
              slotProps={{ textField: { size: 'small', error: !!toError, helperText: toError || ' ' }, field: { clearable: true } }}
            />
          </LocalizationProvider>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={selectedRange}
            onChange={(_, v: QuickRange | null) => { if (v) applyQuickRange(v); }}
            sx={{
              flexWrap: 'wrap',
              gap: 0.75,
              '& .MuiToggleButtonGroup-grouped': {
                border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mx: 0, px: 1.25,
                textTransform: 'none', fontWeight: 650,
              },
              '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
                bgcolor: 'primary.main', borderColor: 'primary.main !important', color: 'primary.contrastText', fontWeight: 800,
              },
            }}
          >
            {QUICK_RANGES.map((r) => <ToggleButton key={r} value={r}>{r}</ToggleButton>)}
          </ToggleButtonGroup>
        </Stack>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          Period: {selectedRange ? `${selectedRange} · ` : 'Custom · '}{periodText} (by settlement date). Cards and table use the same period.
        </Typography>
      </Paper>

      <Typography variant="subtitle2" fontWeight={700} mb={1}>By status</Typography>
      <Grid container spacing={2} mb={1}>
        {STATUS_CARDS.map((c) => {
          const s = statusMap[c.key];
          const selected = status === c.key;
          return (
            <Grid item xs={12} sm={6} md={3} key={c.key}>
              <Paper
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => selectStatus(c.key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectStatus(c.key); } }}
                sx={selectableCardSx(selected, theme.palette[c.color].main)}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {c.label}
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>{summaryLoading ? '…' : s?.count ?? 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Payout {formatMoney(s?.totalPayout ?? 0)}</Typography>
                  </Box>
                  <Box sx={{ color: `${c.color}.main`, display: 'flex' }}>{c.icon}</Box>
                </Stack>
                <Typography variant="caption" color={selected ? `${c.color}.main` : 'text.secondary'} fontWeight={700}>
                  {selected ? 'Showing these records · click to clear' : 'View records →'}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      <Typography variant="caption" color="text.secondary" display="block" mb={3}>
        Total {statusTotalCount} settlements · {formatMoney(statusTotalPayout)} payout (Settled + Pending + Failed + On Hold)
      </Typography>

      <Typography variant="subtitle2" fontWeight={700} mb={1}>By service</Typography>
      {!summaryLoading && modules.length === 0 && (
        <Typography variant="body2" color="text.secondary" mb={3}>No settlements in this period.</Typography>
      )}
      <Grid container spacing={2} mb={1}>
        {modules.map((m) => {
          const selected = vertical === m._id;
          return (
            <Grid item xs={12} sm={6} md={4} lg={12 / Math.min(Math.max(modules.length, 3), 5)} key={m._id}>
              <Paper
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => selectModule(m._id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectModule(m._id); } }}
                sx={selectableCardSx(selected, theme.palette.primary.main)}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={700}>{moduleLabel(m._id)}</Typography>
                <Typography variant="h5" fontWeight={800}>{m.count}</Typography>
                <Typography variant="body2" color="text.secondary">{formatMoney(m.totalPayout)} payout</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Settled {formatMoney(m.settledPayout ?? 0)} · Pending {formatMoney(m.pendingPayout ?? 0)}
                </Typography>
                <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                  <Chip label={`${m.settled ?? 0} Settled`} size="small" color="success" variant="outlined" />
                  <Chip label={`${m.pending ?? 0} Pending`} size="small" color="warning" variant="outlined" />
                  {(m.failed ?? 0) > 0 && <Chip label={`${m.failed} Failed`} size="small" color="error" variant="outlined" />}
                  {(m.onHold ?? 0) > 0 && <Chip label={`${m.onHold} On Hold`} size="small" variant="outlined" />}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      {modules.length > 0 && (
        <Typography variant="caption" color={reconciled ? 'text.secondary' : 'error.main'} display="block" mb={3}>
          Services total {moduleTotalCount} settlements · {formatMoney(moduleTotalPayout)} payout
          {reconciled ? ' — matches the status totals.' : ' — does not match the status totals; refresh the page.'}
        </Typography>
      )}

      <Box ref={tableRef} sx={{ scrollMarginTop: 80 }}>
        <Tabs
          value={vertical}
          onChange={(_, v) => { setVertical(v); setPage(0); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          <Tab label="All" value="" />
          {tabKeys.map((k) => <Tab key={k} label={moduleLabel(k)} value={k} />)}
        </Tabs>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} mb={1.5}>
          <TextField
            size="small"
            label="Search reference"
            placeholder="e.g. ORD-1024"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ minWidth: 240 }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => { setStatus(e.target.value as '' | StatusKey); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUS_CARDS.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {filterChips.map((c) => (
              <Chip key={c.key} label={c.label} onDelete={c.clear} deleteIcon={<Close />} color="primary" variant="outlined" size="small" />
            ))}
          </Stack>
        </Stack>

        {isError && <Alert severity="error" sx={{ mb: 1.5 }}>Could not load settlements. Check your connection and try again.</Alert>}
        {listTotals && (
          <Paper variant="outlined" sx={{ px: 2, py: 1, mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Typography variant="body2"><strong>{listTotals.count}</strong> settlements</Typography>
            <Typography variant="body2">Gross <strong>{formatMoney(listTotals.gross)}</strong></Typography>
            <Typography variant="body2">Commission <strong>{formatMoney(listTotals.commission)}</strong></Typography>
            <Typography variant="body2">Payout <strong>{formatMoney(listTotals.payout)}</strong></Typography>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Totals for the current filters — match the selected card.
            </Typography>
          </Paper>
        )}

        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading || (isFetching && !data)}
          getRowId={(r) => r._id}
          autoHeight
          rowHeight={60}
          disableRowSelectionOnClick
          disableColumnMenu
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize: PAGE_SIZE }}
          onPaginationModelChange={(m) => setPage(m.page)}
          pageSizeOptions={[PAGE_SIZE]}
          localeText={{ noRowsLabel: 'No settlements match these filters' }}
          sx={[adminDataGridSx(theme), { bgcolor: 'background.paper', borderRadius: 2 }] as DataGridProps['sx']}
        />
      </Box>
    </PageShell>
  );
}
